import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Connection, PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import * as XLSX from 'xlsx'; // Import xlsx library
import Header from './Header';
import Footer from './Footer';
import '../styles.css';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminTransactionDetail = () => {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNoPaymentDialog, setShowNoPaymentDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [checkedAddresses, setCheckedAddresses] = useState({ sender: '', receiver: '' });
  const [manualTxHashes, setManualTxHashes] = useState({});
  const [showManualInputs, setShowManualInputs] = useState({});
  const [hideCheckSignature, setHideCheckSignature] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [solPrice, setSolPrice] = useState(null); // Store SOL/USD price
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  const connection = useMemo(() => new Connection(
    process.env.REACT_APP_CHAINSTACK_API_URL || 'https://solana-mainnet.core.chainstack.com/8f1961548a0fd2824b4cbce7bfac5f46',
    'finalized'
  ), []);

  const PRESALE_WALLET = '4mZ9yFABv79h9UNWR3TDSRqegj2Dzi3yZ74DjdySfusc';
  const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
  const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';

  // Fetch SOL/USD price from CoinGecko API
  const fetchSolPrice = useCallback(async () => {
    try {
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      const price = response.data.solana.usd;
      setSolPrice(price);
      return price;
    } catch (err) {
      console.error('Error fetching SOL price:', err);
      setError('Failed to fetch SOL price. Using default value.');
      const defaultPrice = 150; // Fallback price in case API fails
      setSolPrice(defaultPrice);
      return defaultPrice;
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const decodedToken = jwtDecode(token);
      if (!decodedToken.is_admin) {
        navigate('/dashboard');
        return;
      }

      const [usersResponse, transactionsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setUsers(usersResponse.data);
      setTransactions(transactionsResponse.data);
      setFilteredTransactions(transactionsResponse.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Error fetching data: ' + (err.response?.data.error || err.message));
      setLoading(false);
      if (err.response?.status === 403) navigate('/login');
    }
  }, [navigate]);

  // Combined search and status filter functionality
  const applyFilters = (query, status) => {
    let filtered = transactions;

    // Filter by search query (email or wallet address)
    if (query.trim() !== '') {
      filtered = filtered.filter(tx => {
        const user = users.find(u => u.user_id === tx.user_id);
        const emailMatch = user && user.email.toLowerCase().includes(query);
        const walletMatch = tx.wallet_address.toLowerCase().includes(query);
        return emailMatch || walletMatch;
      });
    }

    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(tx => tx.transaction_status.toLowerCase() === status);
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    applyFilters(query, statusFilter);
  };

  const handleStatusFilter = (e) => {
    const status = e.target.value;
    setStatusFilter(status);
    applyFilters(searchQuery, status);
  };

  // Export to Excel functionality
  const exportToExcel = () => {
    // Prepare the data for export
    const exportData = filteredTransactions.map(tx => {
      const user = users.find(u => u.user_id === tx.user_id);
      return {
        'Trn. No.': tx.purchase_id,
        'User Email': user ? user.email : 'Unknown',
        'Stage': tx.stage_id,
        'Quantity': formatNumber(tx.tokens_purchased),
        'Amount': formatNumber(tx.amount),
        'Paid with': tx.payment_coin,
        'Status': tx.transaction_status,
        'TRN. Hash': tx.transaction_hash || 'N/A',
        'Address': tx.wallet_address,
      };
    });

    // Create a worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    // Create a workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, 'Admin_Transactions.xlsx');
  };

  const checkTransaction = useCallback(async (transaction, txHash = null, useManualHash = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login first');
        navigate('/login');
        return { success: false };
      }

      const senderWallet = transaction.wallet_address;
      if (!senderWallet) {
        setError(`Sender wallet not found for purchase ${transaction.purchase_id}`);
        return { success: false };
      }

      let senderATA, receiverATA, selectedMint;
      // Initially assume the payment is USDC/USDT based on the selected paymentCoin
      selectedMint = transaction.payment_coin === 'USDC' ? USDC_MINT : USDT_MINT;
      senderATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(senderWallet));
      receiverATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(PRESALE_WALLET));
      if (!senderATA || !receiverATA) {
        setError(`Failed to compute ATA for sender or receiver for purchase ${transaction.purchase_id}`);
        return { success: false };
      }

      let signatures = [];
      let txHashResult = null;
      let amountPaid = 0;
      let actualSender = null;
      let paymentFound = false;
      let actualPaymentCoin = transaction.payment_coin; // Start with the selected payment coin

      if (useManualHash && txHash) {
        // Manual confirmation with transaction hash
        const hashCheck = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment/check-transaction-hash/${txHash}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (hashCheck.data.isUsed) {
          console.log(`Transaction hash ${txHash} already used for another payment. Skipping.`);
          setError('This transaction hash has already been used. Please provide a different one.');
          return { success: false };
        }
        signatures = [{ signature: txHash, blockTime: null }];
      } else {
        // Automated confirmation
        console.log(`Fetching signatures for wallet ${senderWallet}`);
        const signaturesRes = await connection.getSignaturesForAddress(new PublicKey(senderWallet), { limit: 20 });
        signatures = signaturesRes.slice().reverse();
        console.log(`Found ${signatures.length} signatures for wallet ${senderWallet}`);
      }

      const purchaseCreatedAt = new Date(transaction.created_at).getTime() / 1000;

      // Fetch SOL price at the time of confirmation
      let currentSolPrice = solPrice;
      if (!currentSolPrice) {
        currentSolPrice = await fetchSolPrice();
      }

      for (const sig of signatures) {
        if (!useManualHash) {
          // Filter transactions by timestamp for automated checks
          if (sig.blockTime < purchaseCreatedAt) {
            console.log(`Skipping transaction ${sig.signature} as it occurred before purchase creation (${sig.blockTime} < ${purchaseCreatedAt})`);
            continue;
          }

          const hashCheck = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment/check-transaction-hash/${sig.signature}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (hashCheck.data.isUsed) {
            console.log(`Transaction hash ${sig.signature} already used. Skipping.`);
            continue;
          }
        }

        console.log(`Fetching transaction details for ${sig.signature}`);
        const transactionData = await connection.getParsedTransaction(sig.signature, { 
          commitment: 'finalized',
          maxSupportedTransactionVersion: 0
        });
        if (!transactionData || !transactionData.meta) {
          console.log(`Transaction ${sig.signature} not found or invalid metadata`);
          continue;
        }

        const { meta, transaction: tx } = transactionData;
        if (!meta || !tx) {
          console.log(`Transaction ${sig.signature} missing required data`);
          continue;
        }

        const accountKeys = tx.message.accountKeys.map(key => key.pubkey.toBase58());
        // Use signatures to determine the sender (first signature corresponds to the primary signer)
        const signaturesList = tx.signatures;
        if (!signaturesList || signaturesList.length === 0) {
          console.log(`Transaction ${sig.signature} has no signatures`);
          continue;
        }
        // The sender is typically the first signer
        actualSender = signaturesList[0] ? accountKeys[0] : null;
        if (actualSender !== senderWallet) {
          console.log(`Sender mismatch in transaction ${sig.signature}. Expected: ${senderWallet}, Got: ${actualSender}`);
          continue;
        }

        // First, check if this is a native SOL transfer
        const isNativeTransfer = meta.logMessages.some(log => log.includes('Program 11111111111111111111111111111111 invoke')) &&
                                 !meta.postTokenBalances.length &&
                                 !meta.preTokenBalances.length &&
                                 !meta.innerInstructions?.some(inner => inner.instructions.some(instr => instr.parsed?.type === 'transfer'));
        if (isNativeTransfer) {
          console.log(`Detected SOL transfer in transaction ${sig.signature}`);
          actualPaymentCoin = 'SOL';

          // Calculate SOL amount transferred to PRESALE_WALLET
          const senderPreBalance = meta.preBalances[0] || 0; // First account is the sender
          const senderPostBalance = meta.postBalances[0] || 0;
          const receiverIndex = accountKeys.findIndex(key => key === PRESALE_WALLET);
          const receiverPreBalance = receiverIndex !== -1 ? meta.preBalances[receiverIndex] || 0 : 0;
          const receiverPostBalance = receiverIndex !== -1 ? meta.postBalances[receiverIndex] || 0 : 0;

          // Amount received by PRESALE_WALLET (in lamports)
          const amountReceivedLamports = receiverIndex !== -1 ? receiverPostBalance - receiverPreBalance : 0;
          let solAmountPaid = amountReceivedLamports / 1_000_000_000; // Convert lamports to SOL

          // Deduct transaction fees from sender's balance change to estimate the amount paid
          const fee = meta.fee || 0; // Fee in lamports
          const senderBalanceChange = (senderPreBalance - senderPostBalance - fee) / 1_000_000_000; // Convert to SOL
          if (solAmountPaid <= 0 || senderBalanceChange <= 0) {
            console.log(`No valid SOL transfer amount found in transaction ${sig.signature}`);
            continue;
          }

          // Convert SOL amount to USD using the current SOL price
          amountPaid = solAmountPaid * currentSolPrice;
          console.log(`SOL transfer found in transaction ${sig.signature}: ${amountPaid} USD (${solAmountPaid} SOL at ${currentSolPrice} USD/SOL)`);

          // Special logic for SOL payments to handle volatility
          const expectedAmount = parseFloat(transaction.amount);
          const percentageDifference = (amountPaid - expectedAmount) / amountPaid; // Positive if user amount is lower, negative if higher
          if (percentageDifference > 0 && percentageDifference <= 0.25) {
            // User-entered amount is lower than actual market amount by 25% or less
            console.log(`User amount (${expectedAmount} USD) is ${percentageDifference * 100}% lower than market amount (${amountPaid} USD). Accepting user amount.`);
            amountPaid = expectedAmount;
          } else if (percentageDifference < 0) {
            // User-entered amount is higher than actual market amount
            console.log(`User amount (${expectedAmount} USD) is higher than market amount (${amountPaid} USD). Accepting market amount.`);
            // amountPaid remains as the market amount
          }
        } else {
          // Handle USDC/USDT payments (existing logic)
          const isSenderMatch = accountKeys.some(key => key === senderATA.toBase58());
          const isReceiverMatch = accountKeys.some(key => key === receiverATA.toBase58());
          if (!isSenderMatch || !isReceiverMatch) {
            console.log(`Sender or receiver mismatch in transaction ${sig.signature}. Sender ATA: ${senderATA.toBase58()}, Receiver ATA: ${receiverATA.toBase58()}`);
            continue;
          }

          amountPaid = 0;

          // Check top-level instructions for Transfer or TransferChecked
          if (tx.message.instructions) {
            for (const instr of tx.message.instructions) {
              if (instr.parsed && (instr.parsed.type === 'transfer' || instr.parsed.type === 'transferChecked') && 
                  instr.programId.toBase58() === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' && 
                  instr.parsed.info.mint === selectedMint && 
                  instr.parsed.info.source === senderATA.toBase58() && 
                  (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())) {
                const potentialAmount = instr.parsed.type === 'transfer' 
                  ? parseFloat(instr.parsed.info.amount) / 10 ** 6 
                  : parseFloat(instr.parsed.info.tokenAmount.uiAmount);
                if (potentialAmount > 0.002) {
                  amountPaid = potentialAmount;
                  console.log(`Check: Amount paid from ${instr.parsed.type} instruction: ${amountPaid} in transaction ${sig.signature}`);
                  break;
                } else {
                  console.log(`Check: Ignoring small amount ${potentialAmount} from ${instr.parsed.type} instruction in transaction ${sig.signature}`);
                }
              }
            }
          }

          // Check inner instructions for Transfer
          if (meta.innerInstructions && amountPaid === 0) {
            for (const instruction of meta.innerInstructions) {
              for (const instr of instruction.instructions) {
                if (instr.parsed && instr.parsed.type === 'transfer' && 
                    instr.parsed.info.mint === selectedMint && 
                    instr.parsed.info.source === senderATA.toBase58() && 
                    (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())) {
                  const potentialAmount = parseFloat(instr.parsed.info.amount) / 10 ** 6;
                  if (potentialAmount > 0.002) {
                    amountPaid = potentialAmount;
                    console.log(`Check: Amount paid from inner instructions: ${amountPaid} in transaction ${sig.signature}`);
                    break;
                  } else {
                    console.log(`Check: Ignoring small amount ${potentialAmount} from inner instructions in transaction ${sig.signature}`);
                  }
                }
              }
              if (amountPaid > 0) break;
            }
          }

          if (amountPaid === 0) {
            console.log(`Check: No valid USDC/USDT transfer found in transaction ${sig.signature}`);
            continue;
          }
        }

        const expectedAmount = parseFloat(transaction.amount);
        const lowerBound = expectedAmount * 0.95;
        const upperBound = expectedAmount * 1.05;
        paymentFound = amountPaid >= lowerBound && amountPaid <= upperBound;
        if (paymentFound && !meta.err) {
          txHashResult = sig.signature;
          break;
        } else {
          console.log(`Check: Amount mismatch in transaction ${sig.signature}. Expected range [${lowerBound}, ${upperBound}], got ${amountPaid}`);
          continue;
        }
      }

      if (paymentFound) {
        const stageRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/stages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const stage = stageRes.data.find(s => s.stage_id === transaction.stage_id);
        const pricePerToken = stage?.price_per_token || 0.005;
        if (pricePerToken <= 0) {
          console.error(`Invalid pricePerToken: ${pricePerToken} for stage ${transaction.stage_id}`);
          setError(`Invalid price per token for stage ${transaction.stage_id}`);
          return { success: false };
        }
        const tokensPurchased = amountPaid / pricePerToken;

        const updateData = {
          transaction_status: 'success',
          transaction_hash: txHashResult,
          tokens_purchased: tokensPurchased,
          amount: amountPaid,
          payment_coin: actualPaymentCoin, // Update to SOL if detected
        };
        await axios.put(`${process.env.REACT_APP_API_URL}/api/purchases/${transaction.purchase_id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log(`Updated purchase ${transaction.purchase_id} to success with amount ${amountPaid} and payment coin ${actualPaymentCoin}`);

        setTransactions(prev => prev.map(t => t.purchase_id === transaction.purchase_id ? { ...t, ...updateData } : t));
        setFilteredTransactions(prev => prev.map(t => t.purchase_id === transaction.purchase_id ? { ...t, ...updateData } : t));
        fetchData();
        setCheckedAddresses({ sender: actualSender, receiver: actualPaymentCoin === 'SOL' ? PRESALE_WALLET : receiverATA.toBase58() });
        setShowAddressDialog(true);
        return { success: true };
      } else {
        setError('No valid payment detected for the remaining pending transactions. Please enter the transaction hash and click "Confirm With Hash".');
        setShowNoPaymentDialog(true);
        return { success: false };
      }
    } catch (err) {
      console.error('Check transaction error:', err);
      setError('Error confirming payment: ' + (err.response?.data?.message || err.message));
      setShowAddressDialog(true);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [navigate, connection, fetchData, solPrice, fetchSolPrice]);

  const handleCheckPayments = async () => {
    setLoading(true);
    setError('');

    try {
      const pendingTxs = filteredTransactions.filter(tx => tx.transaction_status.toLowerCase() === 'pending');
      if (pendingTxs.length === 0) {
        setError('No pending transactions to check.');
        setLoading(false);
        return;
      }

      for (const transaction of pendingTxs) {
        const result = await checkTransaction(transaction);
        if (!result.success) {
          setShowManualInputs(prev => ({ ...prev, [transaction.purchase_id]: true }));
          setHideCheckSignature(prev => ({ ...prev, [transaction.purchase_id]: true }));
        }
      }
    } catch (err) {
      console.error('Check payments error:', err);
      setError('Error checking payments: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleManualConfirm = async (transaction) => {
    const txHash = manualTxHashes[transaction.purchase_id] || '';
    if (!txHash.trim()) {
      setError('Please enter a transaction hash for this transaction.');
      return;
    }

    await checkTransaction(transaction, txHash, true);
  };

  const toggleManualInput = (purchaseId) => {
    setShowManualInputs(prev => ({
      ...prev,
      [purchaseId]: !prev[purchaseId],
    }));
    setHideCheckSignature(prev => ({
      ...prev,
      [purchaseId]: true,
    }));
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (num) => {
    return parseFloat(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Check if all transactions are "success"
  const allTransactionsSuccess = filteredTransactions.length > 0 && filteredTransactions.every(tx => tx.transaction_status.toLowerCase() === 'success');

  // Pagination logic
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleGoToPage = (e) => {
    const page = parseInt(e.target.value, 10);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) return <div className="content-container container my-4 text-center">Loading...</div>;
  if (error) return <div className="content-container container my-4 text-center text-danger">{error}</div>;

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <h2 className="text-center mb-4 text-white">Admin Transaction Details</h2>
        {error && <div className="text-danger text-center mb-4">{error}</div>}
        {loading && <div className="text-center mb-4 text-white">Loading...</div>}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <TextField
            label="Search by Email or Wallet Address"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            fullWidth
            style={{ maxWidth: '400px' }}
            sx={{
              '& .MuiInputLabel-root': { color: 'white' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'white' },
                '&:hover fieldset': { borderColor: 'white' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input': { color: 'white' },
            }}
          />
          <FormControl variant="outlined" style={{ minWidth: '200px' }}>
            <InputLabel sx={{ color: 'white' }}>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilter}
              label="Status"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="success">Success</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: '150px' }}>
            <InputLabel sx={{ color: 'white' }}>Items per Page</InputLabel>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              label="Items per Page"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          {!allTransactionsSuccess && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheckPayments}
              disabled={loading || filteredTransactions.filter(tx => tx.transaction_status.toLowerCase() === 'pending').length === 0}
            >
              {loading ? 'Checking...' : 'Check Payments'}
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={exportToExcel}
            disabled={filteredTransactions.length === 0}
          >
            Export to Excel
          </Button>
        </div>
        <Paper className="mb-4">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="hide-on-mobile">Trn. No.</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell className="hide-on-mobile">Paid with</TableCell>
                <TableCell>Status</TableCell>
                <TableCell className="hide-on-mobile">TRN. Hash</TableCell>
                <TableCell className="hide-on-mobile">Address</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.length === 0 ? (
                <TableRow><TableCell colSpan="10" align="center">No transactions found</TableCell></TableRow>
              ) : (
                paginatedTransactions.map(tx => {
                  const user = users.find(u => u.user_id === tx.user_id);
                  return (
                    <TableRow key={tx.purchase_id}>
                      <TableCell className="hide-on-mobile">{tx.purchase_id}</TableCell>
                      <TableCell>{user ? user.email : 'Unknown'}</TableCell>
                      <TableCell>{tx.stage_id}</TableCell>
                      <TableCell>{formatNumber(tx.tokens_purchased)}</TableCell>
                      <TableCell>{formatNumber(tx.amount)}</TableCell>
                      <TableCell className="hide-on-mobile">{tx.payment_coin}</TableCell>
                      <TableCell>{tx.transaction_status}</TableCell>
                      <TableCell className="hide-on-mobile">
                        {tx.transaction_hash ? (
                          <a href={`https://solscan.io/tx/${tx.transaction_hash}`} target="_blank" rel="noopener noreferrer">
                            {tx.transaction_hash.slice(0, 8)}...
                          </a>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell className="hide-on-mobile">{tx.wallet_address.slice(0, 8)}...</TableCell>
                      <TableCell>
                        {tx.transaction_status.toLowerCase() === 'pending' && (
                          <>
                            {!hideCheckSignature[tx.purchase_id] && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => toggleManualInput(tx.purchase_id)}
                                disabled={loading}
                              >
                                Check Signature
                              </Button>
                            )}
                            {showManualInputs[tx.purchase_id] && (
                              <div style={{ marginTop: '10px' }}>
                                <input
                                  type="text"
                                  placeholder="Enter Transaction Hash"
                                  value={manualTxHashes[tx.purchase_id] || ''}
                                  onChange={(e) => setManualTxHashes(prev => ({
                                    ...prev,
                                    [tx.purchase_id]: e.target.value,
                                  }))}
                                  style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
                                />
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleManualConfirm(tx)}
                                  disabled={loading}
                                >
                                  {loading ? 'Verifying...' : 'Confirm with Hash'}
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <FormControl variant="outlined" style={{ minWidth: '100px' }}>
            <InputLabel sx={{ color: 'white' }}>Page</InputLabel>
            <Select
              value={currentPage}
              onChange={handleGoToPage}
              label="Page"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <MenuItem key={page} value={page}>{page}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Go to Page"
            variant="outlined"
            type="number"
            value=""
            onChange={(e) => {
              const page = parseInt(e.target.value, 10);
              if (page >= 1 && page <= totalPages) {
                handlePageChange(page);
              }
            }}
            InputProps={{ inputProps: { min: 1, max: totalPages } }}
            sx={{
              '& .MuiInputLabel-root': { color: 'white' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'white' },
                '&:hover fieldset': { borderColor: 'white' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input': { color: 'white' },
            }}
            style={{ maxWidth: '120px' }}
          />
          <span style={{ alignSelf: 'center' }}>
            of {totalPages}
          </span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </Button>
        </div>

        <Dialog
          open={showNoPaymentDialog}
          TransitionComponent={Transition}
          onClose={() => setShowNoPaymentDialog(false)}
        >
          <DialogTitle>Waiting for Payment</DialogTitle>
          <DialogContent>
            <p>The system is checking for the payment. You can also enter the transaction hash manually below.</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowNoPaymentDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showAddressDialog}
          TransitionComponent={Transition}
          onClose={() => setShowAddressDialog(false)}
        >
          <DialogTitle>Addresses Checked</DialogTitle>
          <DialogContent>
            <p><strong>Sender:</strong> {checkedAddresses.sender}</p>
            <p><strong>Receiver:</strong> {checkedAddresses.receiver}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddressDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/adminDashboard')}
          style={{ marginRight: '10px' }}
        >
          Back to Users
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </section>
      <Footer />
    </div>
  );
};

export default AdminTransactionDetail;
